import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useProject } from "../../context/ProjectContext";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  LayoutDashboard,
  FolderKanban,
  Rss,
  FileText,
  CheckSquare,
  Users,
  UserPlus,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Search,
  BarChart3,
  Globe,
  Settings,
  Bot,
} from "lucide-react";

/* Sidebar items — split by scope.
 * scope: "project"  → depends on the project chosen in the top dropdown
 * scope: "global"   → cross-project / platform-level
 */
const navItems = [
  // ===== PROJECT scope (drives the site you're currently managing) =====
  { path: "/admin", label: "Overview", icon: LayoutDashboard, roles: ["admin", "editor"], scope: "project" },
  { path: "/admin/articles", label: "Articles", icon: FileText, roles: ["admin", "editor"], scope: "project" },
  { path: "/admin/scheduler", label: "AI Scheduler", icon: Bot, roles: ["admin", "editor"], scope: "project" },
  { path: "/admin/reviews", label: "Reviews", icon: CheckSquare, roles: ["admin", "editor"], scope: "project" },
  { path: "/admin/seo", label: "SEO", icon: Search, roles: ["admin", "editor"], scope: "project" },
  { path: "/admin/analytics", label: "Analytics", icon: BarChart3, roles: ["admin", "editor"], scope: "project" },
  { path: "/admin/feeds", label: "API Feeds", icon: Rss, roles: ["admin"], scope: "project" },
  { path: "/admin/applications", label: "Applications", icon: UserPlus, roles: ["admin", "editor"], scope: "project" },

  // ===== GLOBAL scope (across all projects) =====
  { path: "/admin/global-analytics", label: "Global Analytics", icon: Globe, roles: ["admin", "editor"], scope: "global" },
  { path: "/admin/users", label: "Users", icon: Users, roles: ["admin"], scope: "global" },
  { path: "/admin/settings", label: "Global Settings", icon: Settings, roles: ["admin"], scope: "global" },
];

const AdminLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const { projects, currentProject, selectProject } = useProject();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const filtered = navItems.filter((i) => i.roles.includes(user?.role));
  const projectItems = filtered.filter((i) => i.scope === "project");
  const globalItems = filtered.filter((i) => i.scope === "global");
  const onGlobalRoute = globalItems.some((i) => i.path === location.pathname);

  const renderItem = (item, dimmed = false) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => setSidebarOpen(false)}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
          isActive
            ? "bg-[#0F172A] text-white"
            : `text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A] ${dimmed ? "opacity-60" : ""}`
        }`}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <Icon className="h-5 w-5" />
        <span className="font-medium">{item.label}</span>
        {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-body" data-testid="admin-layout">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-[#E2E8F0] z-50 flex items-center justify-between px-4">
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-toggle">
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
        <span className="font-semibold text-[#0F172A]">Admin Panel</span>
        <div className="w-10" />
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-[#E2E8F0] z-40 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="admin-sidebar"
      >
        <div className="h-16 flex items-center px-6 border-b border-[#E2E8F0]">
          <h1 className="text-lg font-bold text-[#0F172A]">MG Admin</h1>
        </div>

        <nav className="p-4 space-y-6 overflow-y-auto" style={{ maxHeight: "calc(100vh - 240px)" }}>
          {/* PROJECT section */}
          <div>
            <div className="flex items-baseline justify-between mb-2 px-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#94A3B8]">
                {currentProject ? currentProject.name : "Project"}
              </p>
              {onGlobalRoute && currentProject && (
                <span className="text-[10px] text-amber-600 font-medium" data-testid="project-context-dim">(inactive)</span>
              )}
            </div>
            <div className="space-y-1">
              {projectItems.map((i) => renderItem(i, onGlobalRoute))}
            </div>
          </div>

          {/* GLOBAL section */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#94A3B8] mb-2 px-2">
              Global
            </p>
            <div className="space-y-1">
              {globalItems.map((i) => renderItem(i))}
            </div>
          </div>
        </nav>

        {/* User Info & Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#E2E8F0] bg-white">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-full bg-[#3B82F6] flex items-center justify-center text-white font-semibold">
              {user?.name?.charAt(0) || "A"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[#0F172A] truncate">{user?.name}</p>
              <p className="text-xs text-[#64748B] truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" className="w-full" onClick={handleLogout} data-testid="logout-button">
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/20 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        {/* Top header — single source of truth for project selection */}
        <header className="h-16 bg-white border-b border-[#E2E8F0] hidden lg:flex items-center justify-between px-8 sticky top-0 z-30">
          <h2 className="text-xl font-semibold text-[#0F172A]">{title}</h2>
          <div className="flex items-center gap-3">
            <label className="text-xs uppercase tracking-wider text-[#94A3B8] font-medium">Project</label>
            {projects.length > 0 ? (
              <Select
                value={currentProject?.id || ""}
                onValueChange={(value) => {
                  const project = projects.find((p) => p.id === value);
                  if (project) selectProject(project);
                }}
              >
                <SelectTrigger className="w-56" data-testid="project-selector">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-sm text-[#94A3B8]">No projects yet</span>
            )}
            {user?.role === "admin" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin/projects")}
                title="Manage all news websites"
                data-testid="manage-projects-btn"
              >
                <FolderKanban className="h-4 w-4 mr-1.5" />
                Manage
              </Button>
            )}
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 md:p-8">{children}</div>
      </main>
    </div>
  );
};

export default AdminLayout;
